export interface BankerReconLeFeeAmountInput {
  name?: string | null;
  borrowerAmount?: number | string | null;
  sellerAmount?: number | string | null;
  totalAmount?: number | string | null;
  borrowerPTC?: number | string | null;
  borrowerOutsideClosing?: number | string | null;
}

export type BankerReconLeFeeBasis = 'at_closing' | 'loan_discount_gross' | 'legacy_gross';

export interface BankerReconLeFeeBreakdown {
  amountAtClosing: number;
  grossAmount: number;
  paidOutsideClosingAmount: number | null;
  basis: BankerReconLeFeeBasis;
}

function toFiniteAmount(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function hasOwn(source: BankerReconLeFeeAmountInput, key: keyof BankerReconLeFeeAmountInput): boolean {
  return Object.prototype.hasOwnProperty.call(source, key);
}

export function getBankerReconLeFeeBreakdown(
  fee: BankerReconLeFeeAmountInput
): BankerReconLeFeeBreakdown {
  const borrowerAmount = toFiniteAmount(fee.borrowerAmount) || 0;
  const totalAmount = toFiniteAmount(fee.totalAmount) || 0;

  // Loan Discount is intentionally sourced from the borrower-paid discount
  // amount. Its total LE field can include unrelated points and is not used.
  if ((fee.name || '').trim().toLowerCase() === 'loan discount') {
    return {
      amountAtClosing: borrowerAmount,
      grossAmount: borrowerAmount,
      paidOutsideClosingAmount: null,
      basis: 'loan_discount_gross'
    };
  }

  if (hasOwn(fee, 'borrowerPTC') && hasOwn(fee, 'borrowerOutsideClosing')) {
    const borrowerPTC = toFiniteAmount(fee.borrowerPTC);
    const borrowerOutsideClosing = toFiniteAmount(fee.borrowerOutsideClosing);

    if (borrowerPTC !== null && borrowerOutsideClosing !== null) {
      return {
        amountAtClosing: borrowerPTC,
        grossAmount: totalAmount || borrowerAmount || borrowerPTC + borrowerOutsideClosing,
        paidOutsideClosingAmount: borrowerOutsideClosing,
        basis: 'at_closing'
      };
    }
  }

  const legacyGrossAmount = borrowerAmount || totalAmount;
  return {
    amountAtClosing: legacyGrossAmount,
    grossAmount: legacyGrossAmount,
    paidOutsideClosingAmount: null,
    basis: 'legacy_gross'
  };
}

export function getBankerReconLeFeeAmount(fee: BankerReconLeFeeAmountInput): number {
  return getBankerReconLeFeeBreakdown(fee).amountAtClosing;
}

/**
 * Total fee dollars collected through closing. Seller-paid dollars are added to the borrower's
 * at-closing amount, while the legacy safeguard avoids double-counting a seller-only fee whose
 * totalAmount already contains the seller share.
 */
export function getBankerReconLeFeeAtClosingTotal(fee: BankerReconLeFeeAmountInput): number {
  const breakdown = getBankerReconLeFeeBreakdown(fee);
  const borrowerAmount = toFiniteAmount(fee.borrowerAmount) || 0;
  const sellerAmount = toFiniteAmount(fee.sellerAmount) || 0;
  const borrowerAtClosing = breakdown.basis === 'legacy_gross' && borrowerAmount === 0 && sellerAmount !== 0
    ? 0
    : breakdown.amountAtClosing;

  return borrowerAtClosing + sellerAmount;
}
